'use client';

import { configureStore } from '@reduxjs/toolkit';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PropsWithChildren, ReactElement } from 'react';
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl';
import { Provider } from 'react-redux';

import { DesignSystemProvider, GlobalStyle, PortalTheme } from '@calm-web/design-system';

import AnalyticsInitializer from '@/components/analytics/AnalyticsInitializer';
import Auth0Provider from '@/components/providers/Auth0Provider';
import BannerProvider from '@/components/providers/BannerProvider';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { rootReducer } from '@/store';

import { FontOverride } from '../FontOverride';
import StyledComponentsRegistry from '../StyledComponentsRegistry';

const cache = createIntlCache();
// Create store singleton outside of component to persist across renders
let store: ReturnType<typeof configureStore> | null = null;
const getStore = (): ReturnType<typeof configureStore> => {
	if (!store) {
		store = configureStore({ reducer: rootReducer });
	}
	return store;
};
const stripePromise = process.env.STRIPE_TOKEN ? loadStripe(process.env.STRIPE_TOKEN) : null;

/**
 * Application-wide providers used by both App Router and Pages Router.
 *
 * The 'use client' directive is required for App Router usage but is
 * safely ignored by Pages Router.
 */
export default function AppProviders({
	children,
	locale = 'en',
	messages,
}: PropsWithChildren<{
	locale?: string;
	messages: Record<string, string>;
}>): ReactElement {
	const intl = createIntl({ locale, messages }, cache);

	return (
		<StyledComponentsRegistry>
			<DesignSystemProvider theme={PortalTheme}>
				<GlobalStyle />
				<FontOverride />
				<Provider store={getStore()}>
					<RawIntlProvider value={intl}>
						<Auth0Provider>
							<Elements stripe={stripePromise}>
								<ErrorBoundary>
									<BannerProvider>
										<AnalyticsInitializer />
										{children}
									</BannerProvider>
								</ErrorBoundary>
							</Elements>
						</Auth0Provider>
					</RawIntlProvider>
				</Provider>
			</DesignSystemProvider>
		</StyledComponentsRegistry>
	);
}
